import { Link } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { thumbUrl, useCollection } from '../app/useSiteData.js'
import { truncate } from '../lib/inline.js'
import { useI18n } from '../lib/i18n.jsx'
import { Card, Chip, Container, EmptyState, ExternalLink, Grid, Media, PageHead, Section } from '../ui/index.js'

/**
 * Demos: what the group's work looks like when it runs.
 *
 * Where a project is described by its grant, a demo is described by its output,
 * so the card leads with a picture rather than with facts. That is the whole
 * difference between this list and the project list, and it is why they are two
 * pages rather than one filtered page.
 *
 * 演示样例：课题组的工作跑起来之后是什么样子。
 *
 * 项目由立项信息描述，演示样例由产出描述，所以这里的卡片以图开场，而不是以事实开场。
 * 这就是本列表与项目列表的全部区别，也是它们分成两个页面而不是一个带筛选的页面的原因。
 */

/** UI string key per demo category. 演示样例类别对应的文案 key。 */
export const DEMO_KIND_KEY = {
  result: 'demos.kindResult',
  engineering: 'demos.kindEngineering',
}

/**
 * One demo card.
 *
 * Shared with the research overview, which shows a truncated version of this
 * same list — one card component means the two places cannot drift apart.
 *
 * 单个演示样例卡片。
 *
 * 与研究方向总览共用，那里展示的是同一份列表的截断版——共用一个卡片组件，
 * 两处就不会各自漂移。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Demo record / 演示样例记录。
 * @returns {JSX.Element} Card / 卡片。
 */
export function DemoCard({ record }) {
  const { t, pick } = useI18n()
  const title = pick(record.title)
  const summary = pick(record.summary)
  const cover = thumbUrl(record.cover)
  const keywords = pick(record.keywords)

  return (
    <Card as="article" className="demo-card" interactive>
      {/* A demo without a cover still gets the same card height, so a grid of
          mixed records does not look broken. The frame is drawn either way.
          没有封面的演示样例仍占同样的卡片高度，混排的栅格因此不会看起来是坏的。
          无论有没有图，画框都在。 */}
      <Media ratio="16 / 9" className="demo-card__media">
        {cover ? (
          <img src={cover} alt={title} loading="lazy" decoding="async" />
        ) : (
          <span className="demo-card__placeholder" aria-hidden="true">
            ▶
          </span>
        )}
      </Media>

      <div className="demo-card__body">
        <p className="demo-card__kind">
          {DEMO_KIND_KEY[record.kind] ? t(DEMO_KIND_KEY[record.kind]) : ''}
          {record.year ? ` · ${record.year}` : ''}
        </p>

        <h3 className="demo-card__title">
          <Link to={`/research/demos/${record.id}`}>{title}</Link>
        </h3>

        {summary && <p className="demo-card__summary">{truncate(summary, 110)}</p>}

        {Array.isArray(keywords) && keywords.length > 0 && (
          <div className="demo-card__keywords">
            {keywords.slice(0, 3).map((keyword) => (
              <Chip key={keyword}>{keyword}</Chip>
            ))}
          </div>
        )}

        {record.codeUrl && (
          <div className="demo-card__links">
            <ExternalLink href={record.codeUrl}>{t('publications.code')}</ExternalLink>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Full demo list.
 *
 * 演示样例完整列表。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function DemoList() {
  const { t } = useI18n()
  const { data: records, loading, error, reload } = useCollection('demos')

  return (
    <Section className="demos-page">
      <Container>
        <nav className="detail-breadcrumb" aria-label={t('nav.research')}>
          <Link className="detail-breadcrumb__link" to="/research">
            {t('nav.research')}
          </Link>
        </nav>

        <PageHead title={t('demos.title')} lede={t('demos.lede')} />

        <PageState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={!records?.length}
          empty={<EmptyState title={t('demos.empty')} body={t('demos.emptyBody')} />}
        >
          <Grid className="demo-grid">
            {(records || []).map((record) => (
              <DemoCard key={record.id} record={record} />
            ))}
          </Grid>
        </PageState>
      </Container>
    </Section>
  )
}
